import React, { useState, useEffect, useRef } from 'react';
import { Search, Command, ChevronDown, Image, Globe, Languages } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SEARCH_ENGINES = [
  { id: 'baidu', name: '百度', url: 'https://www.baidu.com/s?wd=', type: 'web' },
  { id: 'google', name: 'Google', url: 'https://www.google.com/search?q=', type: 'web' },
  { id: 'bing', name: 'Bing', url: 'https://www.bing.com/search?q=', type: 'web' },
];

const SEARCH_MODES = [
    { id: 'web', name: '网页', icon: Globe },
    { id: 'image', name: '图片', icon: Image },
    { id: 'translate', name: '翻译', icon: Languages },
];

interface SearchBarProps {
    placeholder?: string;
}

const SearchBar = ({ placeholder = "Search anything..." }: SearchBarProps) => {
  const [query, setQuery] = useState('');
  const [engine, setEngine] = useState(SEARCH_ENGINES[0]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [mode, setMode] = useState('web'); // web or image
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Typewriter effect state
  const [displayPlaceholder, setDisplayPlaceholder] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        inputRef.current?.blur();
        setIsOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
            setIsOpen(false);
        }
    }

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
        window.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Typewriter Effect Logic
  useEffect(() => {
    const handleTyping = () => {
      const i = loopNum % 1; // Only one placeholder for now, passed via props
      const fullText = placeholder;
      
      setDisplayPlaceholder(
        isDeleting 
          ? fullText.substring(0, displayPlaceholder.length - 1) 
          : fullText.substring(0, displayPlaceholder.length + 1)
      );

      setTypingSpeed(isDeleting ? 50 : 100);

      if (!isDeleting && displayPlaceholder === fullText) {
        setTimeout(() => setIsDeleting(true), 2000); // Pause at end
      } else if (isDeleting && displayPlaceholder === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayPlaceholder, isDeleting, placeholder, loopNum, typingSpeed]);


  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    let searchUrl = engine.url + encodeURIComponent(query);
    
    if (mode === 'translate') {
        searchUrl = `https://fanyi.baidu.com/?query=${encodeURIComponent(query)}&lang=zh2en`;
    } else if (mode === 'image') {
        if (engine.id === 'baidu') {
            searchUrl = `https://image.baidu.com/search/index?tn=baiduimage&word=${encodeURIComponent(query)}`;
        } else if (engine.id === 'google') {
            searchUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`;
        } else if (engine.id === 'bing') {
            searchUrl = `https://www.bing.com/images/search?q=${encodeURIComponent(query)}`;
        }
    }

    window.open(searchUrl, '_blank');
  };

  return (
    <div className="w-full max-w-4xl mx-auto relative z-30" ref={containerRef}>
      <form onSubmit={handleSearch} className={`relative flex items-center transition-all duration-300 ${isFocused ? 'scale-105' : 'scale-100'}`}>
        <div className={`absolute inset-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-2xl transition-all duration-300 ${isFocused ? 'ring-2 ring-blue-500/50 dark:ring-blue-400/50 shadow-blue-500/20' : 'border border-slate-200 dark:border-white/10'}`} />
        
        <div className="relative flex items-center w-full p-2 h-16"> {/* Increased height h-16 */}
          <div className="relative h-full flex items-center">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-5 h-full text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors border-r border-slate-200 dark:border-white/10 whitespace-nowrap"
            >
              <span className="font-medium text-lg">{engine.name}</span>
              <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 mt-2 w-32 bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-xl shadow-xl overflow-hidden z-50"
                >
                  {SEARCH_ENGINES.map((se) => (
                    <button
                      key={se.id}
                      type="button"
                      onClick={() => {
                        setEngine(se);
                        setIsOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white transition-colors text-sm whitespace-nowrap"
                    >
                      {se.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={displayPlaceholder}
            className="flex-1 bg-transparent border-none outline-none px-4 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-xl h-full"
          />

           <div className="flex items-center gap-1 pr-2 border-l border-slate-200 dark:border-white/10 pl-2 h-full">
                {SEARCH_MODES.map((m) => (
                    <button
                        key={m.id}
                        type="button"
                        onClick={() => setMode(m.id)}
                        className={`p-2 rounded-lg transition-colors ${mode === m.id ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                        title={`搜索${m.name}`}
                    >
                        <m.icon size={20} />
                    </button>
                ))}
           </div>

          <button
            type="submit"
            className="h-full aspect-square flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all hover:scale-105 active:scale-95 shadow-lg shadow-blue-600/30 ml-2"
          >
            <Search size={24} />
          </button>
        </div>
      </form>
      
      <div className={`mt-2 text-center transition-opacity duration-300 ${isFocused ? 'opacity-100' : 'opacity-0'}`}>
        <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center justify-center gap-1">
          <Command size={10} /> + K to focus
        </span>
      </div>
    </div>
  );
};

export default SearchBar;
